import React, {useEffect, useRef, useState} from 'react';
import {onAuthStateChanged, signOut} from 'firebase/auth';
import {doc, onSnapshot} from 'firebase/firestore';
import {addBreadcrumb} from '@sentry/react';
import {startSessionAccess} from '../data/sessionAccess';
import BootstrapScreen, {BOOTSTRAP_WATCHDOG_MS} from './BootstrapScreen';

// Wrap the entire app, including cache hydration and page-owned listeners.
// A lapsed lease unmounts those listeners; successful renewal remounts them.
export default function SessionAccessGate({auth, db, renew, children}) {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [accessState, setAccessState] = useState(null);
    const [signingOut, setSigningOut] = useState(false);
    const [signOutError, setSignOutError] = useState(false);
    const [showRecovery, setShowRecovery] = useState(false);
    const controller = useRef();
    useEffect(() => {
        let generation = 0;
        let disposed = false;
        let unsubscribeAccess;
        const unsubscribe = onAuthStateChanged(auth, user => {
            if (disposed) return;
            const current = ++generation;
            controller.current?.stop();
            unsubscribeAccess?.();
            unsubscribeAccess = undefined;
            controller.current = undefined;
            setReady(!user);
            setUser(user);
            setAccessState(null);
            setError(null);
            setSigningOut(false);
            setSignOutError(false);
            setShowRecovery(false);
            if (!user) return;
            const isCurrent = () => !disposed && generation === current && auth.currentUser === user;
            controller.current = startSessionAccess({
                renew,
                onReady: value => { if (isCurrent()) setReady(value); },
                onError: value => { if (isCurrent()) setError(value); }
            });
            // This metadata-only self read remains allowed while protected
            // data is denied. Reopening recreates listeners killed by pending
            // account mutations, including no-op status reconciliation.
            const currentController = controller.current;
            unsubscribeAccess = onSnapshot(doc(db, 'sessionAccess', user.uid), snapshot => {
                if (isCurrent()) {
                    setAccessState(snapshot.data());
                    currentController.observe(snapshot.data());
                }
            }, value => { if (isCurrent()) { setError(value); currentController.retry(); } });
        });
        return () => { disposed = true; generation++; unsubscribe(); unsubscribeAccess?.(); controller.current?.stop(); };
    }, [auth, db, renew]);
    // A lapse denies every protected listener at once, and Sentry keeps only the
    // first of those identical errors. These crumbs put the gate's view beside
    // it: were listeners open, and what did the server record say? Wording
    // avoids "auth"/"session"/"token", which Sentry's scrubber blanks out.
    useEffect(() => {
        if (user) addBreadcrumb({category: 'access-lease', level: 'info', message: `access-lease: listeners ${ready ? 'open' : 'closed'}`});
    }, [ready, user]);
    useEffect(() => {
        if (!accessState) return;
        addBreadcrumb({category: 'access-lease', level: 'info', message: 'access-lease: record changed',
            data: {status: accessState.status, active: accessState.active, expiresInMs: accessState.expiresAt - Date.now()}});
    }, [accessState]);
    useEffect(() => {
        if (!user || ready) {
            setShowRecovery(false);
            return undefined;
        }
        const timer = setTimeout(() => setShowRecovery(true), BOOTSTRAP_WATCHDOG_MS);
        return () => clearTimeout(timer);
    }, [ready, user]);
    if (ready) return children;
    const code = error?.serverCode || error?.code;
    let message = 'Connecting…';
    if (accessState?.status === 'pending') message = 'Your account is being updated. Please wait…';
    else if (accessState?.status === 'failed' || code === 'failed-precondition') message = 'Your account needs administrator attention before you can continue.';
    else if (code === 'aborted') message = 'Your account changed while we were checking it. Retrying…';
    else if (code === 'unavailable' || error?.status === 503 || error?.response?.status === 503) message = 'Sign-in verification is temporarily unavailable. Retrying…';
    else if (code === 'permission-denied' || code === 'unauthenticated') message = 'We could not verify access to your account. Try again or sign out.';
    else if (error) message = 'Unable to connect. Retrying…';
    const retry = () => {
        setError(null);
        controller.current?.retry();
    };
    const leave = async () => {
        if (!user || auth.currentUser !== user) return;
        setSigningOut(true);
        setSignOutError(false);
        try { await signOut(auth); } catch {
            if (auth.currentUser === user) {
                setSigningOut(false);
                setSignOutError(true);
            }
        }
    };
    if (!showRecovery) return <BootstrapScreen />;
    return <BootstrapScreen
        title="We couldn’t finish signing you in"
        message={message}
        onRetry={retry}
        retryLabel="Try again"
        onSecondary={user ? leave : undefined}
        secondaryLabel={signingOut ? 'Signing out…' : 'Sign out'}
        secondaryDisabled={signingOut}
        alert={signOutError ? 'Could not sign out. Please try again.' : null}
    />;
}
